import styled from 'styled-components'
import useWindowSize from '../hooks/useWindowSize';
import { browserName } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

const Button = styled.button`
  width: 100%;
  height: 56px;
  min-height: 56px;
  max-width: ${({ customWidth }) => customWidth || '327px'};
  background-color: ${({ customColor, theme }) => customColor || theme.colors.colorPrimary};
  border-radius: 12px;
  border: none;
  margin: ${({ customMargin }) => customMargin || '24px 0 0'};
  font-weight: ${(props) => props.theme.fontWeights.light};
  font-size: ${(props) => props.theme.fontSizes.font16};
  line-height: ${(props) => props.theme.lineHeights.lineHeight19};
  color: ${(props) => props.theme.colors.colorWhite};
  position: relative;
  z-index: ${({ zIndex }) => zIndex || 'initial'};
  transition: background-color 0.3s;
  ${({ customStyles }) => customStyles};

  &:focus {
    outline: none;
  }

  &:hover {
    opacity: 0.9;
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;

    &:hover {
      cursor: default;
    }
  }
`

const BtnWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;
  height: 100%;
  bottom: 30px;
  @media screen and (min-width: 450px) {
    justify-content: center;
  }
`

const ButtonStub = styled.div`
  height: 56px;
  width: 100%;
  margin-bottom: 16px;
`;

const StickyWrapper = styled.div`
  --bottom-gap: 25px;

  position: ${({ position }) => position || 'fixed'};
  left: auto;
  bottom: ${({ bottomPosition }) => bottomPosition};
  width: 100%;
  z-index: ${({ zIndex }) => zIndex || 'initial'};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media screen and (min-width: 450px) {
    position: ${({ mobilePosition }) => mobilePosition || 'sticky'};
    top: unset;
    align-items: ${({ alignStickyBtn }) => alignStickyBtn || 'center'};
  }

  &:before {
    content: '';
    display: block;
    width: 100vw;
    height: ${({ customBeforeHeight, bottomPosition }) =>
      customBeforeHeight
        ? (`${customBeforeHeight}` || '0px')
        : `calc(100% + var(--bottom-gap) + ${bottomPosition})`};
    position: absolute;
    left: calc(-50vw + 50%);
    top: -10px;
    background: ${({ customBeforeBg }) =>
      customBeforeBg ||
      `linear-gradient(0deg,#fff 0%,#fff 75%,rgba(255,255,255,0.5) 85%,rgba(255,255,255,0.35) 95%,rgba(255,255,255,0) 100%)`};
    pointer-events: none;
  }
`;

const AppButton = ({
  onClick,
  children,
  customText,
  disabled,
  customBeforeBg,
  customBeforeHeight = true,
  customBeforeCmp = null,
  customColor,
  customWidth,
  zIndex,
  customId,
  position,
  mobilePosition,
  displayStickyBtn = true,
  displayStubBtn = true,
  bottomPosition,
  stickyProps,
  alignStickyBtn,
  customMargin,
  customStyles,
  bottomLinks,
}) => {
  const { t } = useTranslation();
  const customInner = customText || 'continue';
  const windowSize = useWindowSize();
  const { innerWidth } = windowSize;
  const onClickFunc = () => {
    if (disabled) return;
    onClick();
  };

  const btn = (
    <>
      <Button
        onClick={onClickFunc}
        disabled={disabled}
        customBeforeBg={customBeforeBg}
        customBeforeHeight={customBeforeHeight}
        customBeforeCmp={customBeforeCmp}
        customColor={customColor}
        customMargin={customMargin}
        customWidth={customWidth}
        zIndex={zIndex}
        id={customId || 'continue-button'}
        customStyles={customStyles}
      >
        {children || t(customInner)}
      </Button>
      {bottomLinks && bottomLinks()}
    </>
  );

  const stickyContent = (
    <StickyWrapper
      position={position}
      mobilePosition={mobilePosition}
      bottomPosition={bottomPosition}
      zIndex={zIndex}
      customBeforeBg={customBeforeBg}
      customBeforeHeight={customBeforeHeight}
      customBeforeCmp={customBeforeCmp}
      alignStickyBtn={alignStickyBtn}
      {...stickyProps}
    >
      {btn}
    </StickyWrapper>
  );

  return (
    <>
      {customBeforeCmp !== null ? (
        <BtnWrapper>
          {customBeforeCmp}
          {displayStickyBtn && (bottomPosition || stickyProps) ? (
            <>{stickyContent}</>
          ) : (
            btn
          )}
        </BtnWrapper>
      ) : (
        displayStickyBtn && (bottomPosition || stickyProps) ? (
          <>{stickyContent}</>
        ) : (
          btn
        )
      )}
      {(((browserName === 'Facebook' || browserName === 'Instagram') &&
        displayStubBtn) ||
        (innerWidth <= 450 && displayStubBtn)) && <ButtonStub />}
    </>
  );
};

export default AppButton
